import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Alert } from '@mui/material';
import { employeeAPI } from '../services/api';
import { setupImageHandlers } from '../utils/imageUtils';
import { getImageUrl } from '../utils/urlUtils';
import '../styles/EmployeeCard.css';

const EmployeeProfileNew = () => {
  const { employee_code } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const response = await employeeAPI.getByCode(employee_code);
      setEmployee(response.data.employee);
    } catch (err) {
      setError('Employee not found or inactive');
    } finally {
      setLoading(false);
    }
  }, [employee_code]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  useEffect(() => {
    if (employee) {
      const t = setTimeout(() => setupImageHandlers(), 100);
      return () => clearTimeout(t);
    }
  }, [employee]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const raw = String(dateString).trim();
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '-');
  };

  const fullName = employee
    ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim().toUpperCase()
    : '';
  const designation = employee
    ? (employee.designation || employee.position || 'EMPLOYEE').toUpperCase()
    : '';
  const photoSrc = employee?.photo_url ? getImageUrl(employee.photo_url) : null;
  const qrSrc = employee?.qr_code_url ? getImageUrl(employee.qr_code_url) : null;

  if (loading) {
    return (
      <div className="employee-card-page">
        <div className="card-page-state">
          <CircularProgress sx={{ color: '#0B1D4A' }} />
          <p>Loading identity card…</p>
        </div>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="employee-card-page">
        <div className="card-page-state">
          <Alert severity="error" sx={{ maxWidth: 360, borderRadius: 2 }}>
            {error || 'Employee profile not found'}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-card-page">
      <div className="card-page-bg" aria-hidden="true" />

      <header className="card-page-header">
        <img src="/images/logo.png" alt="" className="card-page-header-logo" />
        <div>
          <p className="card-page-kicker">Star Security Services</p>
          <h1 className="card-page-title">Digital Identity Card</h1>
        </div>
      </header>

      <div className="employee-card-container">
        {/* Front */}
        <article className="card-face-wrap">
          <span className="card-face-label">Front</span>
          <div
            className="identity-card-one"
            style={{
              backgroundImage: 'url(/images/background-image-1.png)',
              backgroundSize: '700px',
              backgroundPosition: 'center',
            }}
          >
            <img className="elementOne" src="/images/element-1.png" alt="" />
            <img
              className="elementTwo"
              src="/images/element_image_cross.png"
              alt=""
            />
            <div className="container">
              <div className="logo-container">
                <img className="logo" src="/images/logo.png" alt="Logo" />
                <img
                  className="company_name"
                  src="/images/company_name.png"
                  alt="Star Security Services"
                />
              </div>
              {photoSrc ? (
                <img
                  className="profile-img"
                  src={photoSrc}
                  alt={fullName}
                />
              ) : (
                <div className="profile-img empty-profile" aria-label="No photo" />
              )}
              <div className="info-container">
                <h2 className="security-name">{fullName}</h2>
                <p className="security-position">{designation}</p>
                <div className="card">
                  <div className="row">
                    <div className="label">ID NO</div>
                    <div className="separator">:</div>
                    <div className="value">{employee.employee_code}</div>
                  </div>
                  <div className="row">
                    <div className="label">D.O.B</div>
                    <div className="separator">:</div>
                    <div className="value">{formatDate(employee.date_of_birth)}</div>
                  </div>
                  <div className="row">
                    <div className="label">D.O.J</div>
                    <div className="separator">:</div>
                    <div className="value">{formatDate(employee.hire_date)}</div>
                  </div>
                  <div className="row">
                    <div className="label">D.O.Issue</div>
                    <div className="separator">:</div>
                    <div className="value">{formatDate(employee.date_of_issue)}</div>
                  </div>
                  <div className="row">
                    <div className="label">MO. NO</div>
                    <div className="separator">:</div>
                    <div className="value">{employee.phone || 'N/A'}</div>
                  </div>
                  <div className="row row-address">
                    <div className="label">ADDRESS</div>
                    <div className="separator">:</div>
                    <div className="value">{employee.address || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Back */}
        <article className="card-face-wrap">
          <span className="card-face-label">Back</span>
          <div
            className="identity-card-two"
            style={{
              backgroundImage: 'url(/images/background-image-1.png)',
              backgroundSize: '700px',
              backgroundPosition: 'center',
            }}
          >
            <div className="card-two">
              <div className="header">
                <img
                  src="/images/logo.png"
                  alt="Star Security"
                  className="logo-1"
                />
                <div className="title">
                  <h2>STAR SECURITY SERVICES</h2>
                  <p>
                    Office Contact No: <span>02568-297234</span>
                  </p>
                </div>
              </div>
              <div className="rules-title">* नियम व अटी *</div>
              <ul className="rules">
                <li>
                  <b>ओळख पत्र बाळगणे:</b> कामाच्या वेळेत ओळख पत्र नेहमी स्वत:कडे
                  असणे बंधनकारक. नसल्यास प्रवेश बंदी.
                </li>
                <li>
                  <b>अधिकृत वापर:</b> ओळख पत्र फक्त वैध वापरासाठी. इतरांना वापर देऊ
                  नये.
                </li>
                <li>
                  <b>गमावल्यास:</b> हरवले किंवा खराब झाल्यास त्वरित ऑफिसमध्ये
                  कळवावे.
                </li>
                <li>
                  <b>कार्ड परत देणे:</b> नोकरी संपल्यावर ओळख पत्र परत करणे बंधनकारक.
                  परत न केल्यास अंतिम पगार रोखला जाईल.
                </li>
                <li>
                  <b>फक्त अंतर्गत वापरासाठी:</b> ओळख पत्र फक्त कंपनीच्या कामासाठीच
                  वापरावे. बाहेरील व्यक्ती वा गैरवापर अवैध आहे.
                </li>
              </ul>

              <div className="authority">
                <img
                  className="signature"
                  src="/images/patve_sign.png"
                  alt="Issuing authority signature"
                />
                <span>Issuing Authority</span>
              </div>

              <div className="barcode">
                {qrSrc ? (
                  <img src={qrSrc} alt="Employee QR code" className="qr-img" />
                ) : (
                  <img src="/images/barcode.png" alt="Barcode" />
                )}
              </div>
              <div className="scan-text">
                {qrSrc ? 'Scan to verify employee identity' : 'Scan for company website'}
              </div>

              <div className="emergency">
                <h4>Emergency Contact Number</h4>
                <p>Police - 100 | Ambulance - 108 | Fire - 101 | Emergency - 112</p>
                <p>
                  <b>
                    Rane Nagar, Nizampur, Tal. Sakri, Dist. Dhule, PIN-424305.
                  </b>
                </p>
              </div>
            </div>
          </div>
        </article>
      </div>

      <footer className="card-page-footer">
        Official digital identity · Star Security Services
      </footer>
    </div>
  );
};

export default EmployeeProfileNew;
